import { useEffect, useState } from 'react';

export const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
})();

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Parse a cron expression into a friendly schedule object. Falls back to
// {mode:'custom', custom: <raw>} when the expression doesn't match a simple
// pattern -- so power users can still hand-write anything croniter accepts.
export function parseCron(expr) {
  const parts = (expr || '').trim().split(/\s+/);
  if (parts.length !== 5) return { mode: 'daily', hour: 9, minute: 0 };
  const [min, hour, dom, month, dow] = parts;
  const isInt = (s) => /^\d+$/.test(s);
  if (min.startsWith('*/') && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    const n = parseInt(min.slice(2), 10);
    if (Number.isFinite(n) && n > 0) return { mode: 'minutes', minutes: n };
  }
  if (isInt(min) && hour.startsWith('*/') && dom === '*' && month === '*' && dow === '*') {
    const n = parseInt(hour.slice(2), 10);
    if (Number.isFinite(n) && n > 0) return { mode: 'hourly', hours: n, minute: parseInt(min, 10) };
  }
  if (isInt(min) && isInt(hour) && dom === '*' && month === '*' && dow === '*') {
    return { mode: 'daily', hour: parseInt(hour, 10), minute: parseInt(min, 10) };
  }
  if (isInt(min) && isInt(hour) && dom === '*' && month === '*' && dow !== '*') {
    const days = parseDow(dow);
    if (days) return { mode: 'weekly', hour: parseInt(hour, 10), minute: parseInt(min, 10), days };
  }
  if (isInt(min) && isInt(hour) && isInt(dom) && month === '*' && dow === '*') {
    return { mode: 'monthly', hour: parseInt(hour, 10), minute: parseInt(min, 10), day: parseInt(dom, 10) };
  }
  return { mode: 'custom', custom: expr };
}

function parseDow(s) {
  if (s.includes('-')) {
    const [a, b] = s.split('-').map((x) => parseInt(x, 10));
    if (Number.isFinite(a) && Number.isFinite(b) && a <= b) {
      const out = [];
      for (let i = a; i <= b; i++) out.push(i % 7);
      return out;
    }
  }
  if (s.includes(',')) {
    const out = s.split(',').map((x) => parseInt(x, 10)).filter((x) => Number.isFinite(x) && x >= 0 && x <= 6);
    return out.length ? out : null;
  }
  if (/^\d+$/.test(s)) return [parseInt(s, 10) % 7];
  return null;
}

export function buildCron(s) {
  switch (s.mode) {
    case 'minutes': return `*/${Math.max(1, s.minutes || 1)} * * * *`;
    case 'hourly': return `${s.minute || 0} */${Math.max(1, s.hours || 1)} * * *`;
    case 'daily': return `${s.minute || 0} ${s.hour || 0} * * *`;
    case 'weekly': {
      const days = (s.days && s.days.length ? [...s.days].sort((a, b) => a - b) : [1]).join(',');
      return `${s.minute || 0} ${s.hour || 0} * * ${days}`;
    }
    case 'monthly': return `${s.minute || 0} ${s.hour || 0} ${Math.max(1, Math.min(31, s.day || 1))} * *`;
    case 'custom': return (s.custom || '').trim();
    default: return '';
  }
}

export function describeCron(expr, tz) {
  if (!expr) return '';
  const s = parseCron(expr);
  const fmtTime = (h, m) => {
    const d = new Date();
    d.setHours(h || 0, m || 0, 0, 0);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };
  const tzShort = (() => {
    try {
      return new Intl.DateTimeFormat([], { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || tz;
    } catch { return tz; }
  })();
  switch (s.mode) {
    case 'minutes': return `Every ${s.minutes} minute${s.minutes === 1 ? '' : 's'}`;
    case 'hourly': return `Every ${s.hours} hour${s.hours === 1 ? '' : 's'} at :${String(s.minute).padStart(2, '0')}`;
    case 'daily': return `Every day at ${fmtTime(s.hour, s.minute)} ${tzShort}`;
    case 'weekly': {
      const days = s.days.map((d) => WEEKDAY_LABELS[d]).join(', ');
      return `${days} at ${fmtTime(s.hour, s.minute)} ${tzShort}`;
    }
    case 'monthly': return `Day ${s.day} of the month at ${fmtTime(s.hour, s.minute)} ${tzShort}`;
    case 'custom': return `Custom cron: ${s.custom}`;
    default: return expr;
  }
}

export default function SchedulePanel({ cronExpression, cronTimezone, onChange, compact = false }) {
  const parsed = parseCron(cronExpression);
  const initial = (() => {
    if (parsed.mode === 'custom' || parsed.mode === undefined) {
      return { mode: parsed.mode || 'daily', minutes: 5, hours: 1, hour: 9, minute: 0, days: [1, 2, 3, 4, 5], day: 1, custom: parsed.custom || cronExpression || '' };
    }
    return {
      mode: parsed.mode,
      minutes: parsed.minutes ?? 5,
      hours: parsed.hours ?? 1,
      hour: parsed.hour ?? 9,
      minute: parsed.minute ?? 0,
      days: parsed.days ?? [1, 2, 3, 4, 5],
      day: parsed.day ?? 1,
      custom: parsed.custom ?? '',
    };
  })();
  const [s, setS] = useState(initial);
  const [tz, setTz] = useState(cronTimezone || BROWSER_TZ);

  useEffect(() => {
    if (!cronExpression) return;
    const p = parseCron(cronExpression);
    setS((prev) => ({
      ...prev,
      mode: p.mode,
      minutes: p.minutes ?? prev.minutes,
      hours: p.hours ?? prev.hours,
      hour: p.hour ?? prev.hour,
      minute: p.minute ?? prev.minute,
      days: p.days ?? prev.days,
      day: p.day ?? prev.day,
      custom: p.custom ?? prev.custom,
    }));
  }, [cronExpression]);

  useEffect(() => { setTz(cronTimezone || BROWSER_TZ); }, [cronTimezone]);

  const update = (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    onChange(buildCron(next), tz);
  };
  const setTzAndSave = (newTz) => {
    setTz(newTz);
    onChange(buildCron(s), newTz);
  };
  const timeValue = `${String(s.hour).padStart(2, '0')}:${String(s.minute).padStart(2, '0')}`;
  const onTimeChange = (e) => {
    const [h, m] = e.target.value.split(':').map((x) => parseInt(x, 10));
    update({ hour: Number.isFinite(h) ? h : 0, minute: Number.isFinite(m) ? m : 0 });
  };
  const toggleDay = (d) => {
    const has = s.days.includes(d);
    update({ days: has ? s.days.filter((x) => x !== d) : [...s.days, d] });
  };

  const computed = buildCron(s);
  const description = describeCron(computed, tz);

  const tzOptions = Array.from(new Set([
    BROWSER_TZ,
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Tokyo',
    'Australia/Sydney', 'UTC',
  ]));

  const fieldRow = { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' };
  const numInput = { width: '90px' };
  const selectStyle = { width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#e2e8f0', fontSize: '14px' };

  const Wrap = ({ children }) => compact ? <div>{children}</div> : <div className="lw-panel">{children}</div>;
  const Card = ({ children, accent }) => compact
    ? <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>{children}</div>
    : <div className="lw-card" style={accent ? { borderColor: 'rgba(168, 85, 247, 0.3)', background: 'rgba(168, 85, 247, 0.05)' } : undefined}><div className="lw-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>{children}</div></div>;

  return (
    <Wrap>
      <Card>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', color: '#94a3b8', fontSize: '13px' }}>Run this listener</label>
          <select value={s.mode} onChange={(e) => update({ mode: e.target.value })} style={selectStyle}>
            <option value="minutes">Every N minutes</option>
            <option value="hourly">Every N hours</option>
            <option value="daily">Every day at a specific time</option>
            <option value="weekly">On chosen weekdays at a specific time</option>
            <option value="monthly">On a specific day of the month</option>
            <option value="custom">Custom (cron expression)</option>
          </select>
        </div>

        {s.mode === 'minutes' && (
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#94a3b8', fontSize: '13px' }}>Interval</label>
            <div style={fieldRow}>
              <span>Every</span>
              <input type="number" min={1} max={1440} style={numInput}
                value={s.minutes}
                onChange={(e) => update({ minutes: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
              <span>minutes</span>
            </div>
          </div>
        )}

        {s.mode === 'hourly' && (
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#94a3b8', fontSize: '13px' }}>Hours and minute offset</label>
            <div style={fieldRow}>
              <span>Every</span>
              <input type="number" min={1} max={24} style={numInput}
                value={s.hours}
                onChange={(e) => update({ hours: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
              <span>hour(s), at minute</span>
              <input type="number" min={0} max={59} style={numInput}
                value={s.minute}
                onChange={(e) => update({ minute: Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0)) })} />
            </div>
          </div>
        )}

        {(s.mode === 'daily' || s.mode === 'weekly' || s.mode === 'monthly') && (
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#94a3b8', fontSize: '13px' }}>Time of day</label>
            <input type="time" value={timeValue} onChange={onTimeChange} style={{ width: '160px', fontSize: '16px' }} />
            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '6px' }}>Interpreted in the timezone chosen below.</div>
          </div>
        )}

        {s.mode === 'weekly' && (
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#94a3b8', fontSize: '13px' }}>Days of the week</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {WEEKDAY_LABELS.map((lbl, i) => {
                const active = s.days.includes(i);
                return (
                  <button
                    key={i} type="button" onClick={() => toggleDay(i)}
                    style={{
                      padding: '8px 14px', borderRadius: '8px',
                      border: `1px solid ${active ? '#c084fc' : 'rgba(255,255,255,0.1)'}`,
                      background: active ? 'rgba(168, 85, 247, 0.18)' : 'rgba(255,255,255,0.04)',
                      color: active ? '#c084fc' : '#94a3b8',
                      cursor: 'pointer', fontWeight: 600,
                    }}
                  >{lbl}</button>
                );
              })}
            </div>
          </div>
        )}

        {s.mode === 'monthly' && (
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#94a3b8', fontSize: '13px' }}>Day of the month</label>
            <input type="number" min={1} max={31} style={numInput}
              value={s.day}
              onChange={(e) => update({ day: Math.max(1, Math.min(31, parseInt(e.target.value, 10) || 1)) })} />
            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '6px' }}>Months shorter than this day are skipped (e.g. day 31 in February).</div>
          </div>
        )}

        {s.mode === 'custom' && (
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: '#94a3b8', fontSize: '13px' }}>Cron expression</label>
            <input
              type="text" value={s.custom}
              onChange={(e) => update({ custom: e.target.value })}
              placeholder="e.g., 0 9 * * 1-5"
              style={{ fontFamily: 'monospace', fontSize: '16px', letterSpacing: '2px', width: '100%' }}
            />
            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '6px' }}>
              5-field cron: <code>minute hour day-of-month month day-of-week</code>.
              {' '}<a href="https://crontab.guru" target="_blank" rel="noopener noreferrer" style={{ color: '#c084fc' }}>crontab.guru</a> can help.
            </div>
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '6px', color: '#94a3b8', fontSize: '13px' }}>Timezone</label>
          <select value={tz} onChange={(e) => setTzAndSave(e.target.value)} style={selectStyle}>
            {tzOptions.map((z) => (
              <option key={z} value={z}>{z === BROWSER_TZ ? `${z}  (your computer's time)` : z}</option>
            ))}
          </select>
        </div>
      </Card>

      {computed && (
        <Card accent>
          <div style={{ color: '#c084fc', fontWeight: 600, fontSize: '14px' }}>{description}</div>
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>
            Cron: <code style={{ background: 'rgba(168, 85, 247, 0.18)', padding: '2px 6px', borderRadius: '4px' }}>{computed}</code>
          </div>
        </Card>
      )}
    </Wrap>
  );
}
